import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { FormHelperText } from '@material-ui/core';
import { ConfigFieldControl, StatusLabel } from '@sierralabs/core-ui';
import { TaskStatusLabel, useFindTask } from '@sierralabs/workflow-ui';
import SiteQueryFieldPicker from '../../common/pickers/SiteQueryFieldPicker';
import { useConfigComponent } from '../../hooks/component/useConfigComponent';
import { useGetQueriesForComponentTree } from '../../hooks/component/useGetQueriesForComponentTree';
import { useQueryValue } from '../../hooks/component/useQueryDisplayValue';
import { useSubscribeComponentChanges } from '../../hooks/component/useSubscribeComponentChanges';
import { useSiteContext } from '../../SiteContext';
import { SiteComponent } from '../SiteComponentPlugin';
const StyledConfigFieldControl = styled(ConfigFieldControl) `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;
/**
 * NOTE: Only used to avoid breaking existing configuration for
 * `SiteTaskStatusLabelPlugin` using `queryId` and `queryField` (taskId).
 * @deprecated Use new configuration instead by removing / readding the component.
 */
function useLegacyTask(cProps, props) {
    const isLegacy = !(cProps.status && cProps.workflowId);
    const taskIdValue = useQueryValue(cProps.queryId, cProps.queryField, props.queryValueProps);
    const taskId = isLegacy ? taskIdValue : undefined;
    const { task: foundTask, findTask } = useFindTask(taskId);
    const [task, setTask] = useState(foundTask);
    useEffect(() => {
        // unset when taskId is changed/removed to reload task
        setTask(undefined);
        taskId && findTask();
    }, [taskId, findTask]);
    useEffect(() => {
        foundTask && setTask(foundTask);
    }, [foundTask]);
    return task;
}
export const SiteTaskStatusLabel = props => {
    var _a, _b, _c, _d;
    const component = useSubscribeComponentChanges(SiteTaskStatusLabelPlugin, props);
    const { editing, isHover, hoverProps } = useSiteContext();
    const classes = ['site-task-status-label'];
    if (isHover(component)) {
        classes.push('component-hover');
    }
    const cProps = component.props || {};
    // get field values based on configuration
    const status = useQueryValue((_a = cProps.status) === null || _a === void 0 ? void 0 : _a.queryId, (_b = cProps.status) === null || _b === void 0 ? void 0 : _b.queryField, props.queryValueProps);
    const workflowId = useQueryValue((_c = cProps.workflowId) === null || _c === void 0 ? void 0 : _c.queryId, (_d = cProps.workflowId) === null || _d === void 0 ? void 0 : _d.queryField, props.queryValueProps);
    const taskValue = useMemo(() => {
        return status || workflowId ? { status, workflowId } : undefined;
    }, [status, workflowId]);
    // use legacyTask if component is still using taskId field configuration
    const legacyTask = useLegacyTask(cProps, props);
    const task = taskValue || legacyTask;
    const content = task ? React.createElement(TaskStatusLabel, { task: task }) : React.createElement(StatusLabel, null);
    return (React.createElement(SiteComponent, Object.assign({ plugin: SiteTaskStatusLabelPlugin }, props), editing ? (React.createElement("div", Object.assign({ className: classes.join(' ') }, hoverProps(component)), content)) : (content)));
};
export const SiteTaskStatusLabelConfigurator = props => {
    var _a, _b, _c, _d, _e, _f, _g, _h;
    const { component, update } = useConfigComponent(props.component);
    const queries = useGetQueriesForComponentTree(component);
    return (React.createElement(React.Fragment, null,
        React.createElement(StyledConfigFieldControl, null,
            React.createElement(SiteQueryFieldPicker, { options: queries, label: 'Status Field', queryId: (_b = (_a = component.props) === null || _a === void 0 ? void 0 : _a.status) === null || _b === void 0 ? void 0 : _b.queryId, queryField: (_d = (_c = component.props) === null || _c === void 0 ? void 0 : _c.status) === null || _d === void 0 ? void 0 : _d.queryField, onChange: value => {
                    update('props.status', value.queryId ? value : undefined);
                } }),
            React.createElement(FormHelperText, null, "Select the Task Status field")),
        React.createElement(StyledConfigFieldControl, null,
            React.createElement(SiteQueryFieldPicker, { options: queries, label: 'Workflow ID Field', queryId: (_f = (_e = component.props) === null || _e === void 0 ? void 0 : _e.workflowId) === null || _f === void 0 ? void 0 : _f.queryId, queryField: (_h = (_g = component.props) === null || _g === void 0 ? void 0 : _g.workflowId) === null || _h === void 0 ? void 0 : _h.queryField, onChange: value => {
                    update('props.workflowId', value.queryId ? value : undefined);
                } }),
            React.createElement(FormHelperText, null, "Select the Workflow ID field"))));
};
export const SiteTaskStatusLabelPlugin = {
    name: 'Task Status Label',
    type: 'site-task-status-label',
    component: SiteTaskStatusLabel,
    configurator: SiteTaskStatusLabelConfigurator
};
//# sourceMappingURL=SiteTaskStatusLabelPlugin.js.map